import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";
import type { SessionInfo } from "@shared/api";
import { SessionInfoSchema } from "@shared/api";
import { apiFetch } from "./client";

/**
 * OWNED BY C1. Session state from GET /api/me (S1/S2). Offline grace (F-106): if the fetch fails
 * while the browser is offline we keep the last session we saw rather than throwing the operator out
 * of a working register mid-market.
 */

export type SessionState =
  | { status: "loading"; session: null }
  | { status: "signed-in"; session: SessionInfo }
  | { status: "signed-out"; session: null }
  | { status: "offline"; session: SessionInfo | null };

export interface SessionContextValue {
  state: SessionState;
  refresh: () => Promise<void>;
}

const SessionContext = createContext<SessionContextValue | null>(null);
const CACHE_KEY = "wc.session";

function readCache(): SessionInfo | null {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const parsed = SessionInfoSchema.safeParse(JSON.parse(raw));
    return parsed.success ? parsed.data : null;
  } catch {
    return null;
  }
}

/** Whether this device remembers a signed-in session, before /api/me has answered (D-055 uses it to decide
 *  whether to preload the landing page's pitch). */
export function hasCachedSession(): boolean {
  try {
    return localStorage.getItem(CACHE_KEY) !== null;
  } catch {
    return false;
  }
}

function writeCache(session: SessionInfo | null): void {
  try {
    if (session) localStorage.setItem(CACHE_KEY, JSON.stringify(session));
    else localStorage.removeItem(CACHE_KEY);
  } catch { /* private mode: the session simply is not cached */ }
}

export function SessionProvider({ children, fetchSession }: {
  children: ReactNode;
  /** Injected in tests. */
  fetchSession?: () => Promise<SessionInfo>;
}) {
  const [state, setState] = useState<SessionState>({ status: "loading", session: null });

  const load = useCallback(async () => {
    const get = fetchSession ?? (() => apiFetch<unknown>("/api/me").then((raw) => SessionInfoSchema.parse(raw)));
    try {
      const session = await get();
      writeCache(session);
      setState({ status: "signed-in", session });
    } catch (err) {
      const status = (err as { status?: number }).status;
      if (status === 401 || status === 403) {
        writeCache(null);
        setState({ status: "signed-out", session: null });
        return;
      }
      setState({ status: "offline", session: readCache() });
    }
  }, [fetchSession]);

  useEffect(() => { void load(); }, [load]);

  const value = useMemo<SessionContextValue>(() => ({ state, refresh: load }), [state, load]);
  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): SessionContextValue {
  const ctx = useContext(SessionContext);
  if (!ctx) throw new Error("useSession must be used inside <SessionProvider>");
  return ctx;
}

/** The session when there is a provider, else null: for screens that also render on their own in tests. */
export function useOptionalSession(): SessionContextValue | null {
  return useContext(SessionContext);
}
